import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import {
  OrganisationService,
  PlanChangePreview,
} from '../../services/organisation.service';
import { ProductService } from '../../services/product.service';
import { ConfirmDialogComponent } from '../dialogs/confirm-dialog/confirm-dialog.component';
import { BillingDetailsDialogComponent } from '../dialogs/billing-details-dialog/billing-details-dialog.component';
import { PlanUpgradeDialogComponent } from '../dialogs/plan-upgrade-dialog/plan-upgrade-dialog.component';
import { isFreemailAddress } from '../../utils/trial-eligibility';

export interface TempProduct {
  id: string;
  stripePriceId?: string;
  name: string;
  description: string;
  price: number;
  /** Annual price in cents. Present only for plans that offer a yearly option
   *  (basic, professional). The backend resolves the actual Stripe annual price
   *  by the `interval` param; this is display-only. */
  annualPrice?: number;
  isSelected: boolean;
  items: string;
  hasTrial?: boolean;
  isVisible?: boolean;
  /** Analytics retention (days) — mirrors plans.analytics_retention_days
   *  (al-tutor migration 106) resp. the env default for plans without a
   *  value. Used only for the downgrade warning dialog. */
  retentionDays?: number;
}

@Component({
  selector: 'app-product-card',
  templateUrl: './product-card.component.html',
  styleUrl: './product-card.component.scss',
})
export class ProductCardComponent {
  @Input() product!: TempProduct;
  /** Billing interval selected by the page-level monthly/annual toggle. */
  @Input() billingInterval: 'month' | 'year' = 'month';

  /** Whether this plan actually offers an annual price. */
  get hasAnnual(): boolean {
    return this.product.annualPrice != null;
  }

  /** Only bill yearly when the toggle says so AND this plan has an annual price;
   *  plans without one (free, enterprise) always fall back to monthly. */
  get effectiveInterval(): 'month' | 'year' {
    return this.billingInterval === 'year' && this.hasAnnual ? 'year' : 'month';
  }

  constructor(
    protected organisationService: OrganisationService,
    protected productService: ProductService,
    protected authService: AuthService,
    protected router: Router,
    protected t: TranslateService,
    protected dialogService: NbDialogService,
    protected nbToastrService: NbToastrService
  ) {}

  /** Number formatting follows the UI language, not LOCALE_ID — that one is
   *  derived from `navigator.language` in app.module, so a German browser
   *  running the portal in English would otherwise render "2.388" there. */
  private get numberLocale(): string {
    const lang = this.t.currentLang || this.t.defaultLang || 'de';
    return lang.startsWith('en') ? 'en-US' : 'de-DE';
  }

  /** "," for de-DE, "." for en-US — the cent block is rendered separately. */
  get decimalSeparator(): string {
    return this.numberLocale === 'en-US' ? '.' : ',';
  }

  /** Amount in cents for the selected interval. These are NET figures: every
   *  offered Stripe price is tax_behavior=exclusive, so VAT is added on top at
   *  checkout and the card shows the net price with a "plus VAT" note.
   *  See features/docs/technical/stripe-accounts-and-modes.md. */
  private get netCents(): number {
    return this.effectiveInterval === 'year' && this.product.annualPrice != null
      ? this.product.annualPrice
      : this.product.price;
  }

  /** Whether to render the "plus VAT" note — not for Free (0) or Enterprise (-1). */
  get showsVatNote(): boolean {
    return this.product.price > 0;
  }

  getEuroPrice() {
    if (this.netCents === 0) {
      return '0';
    }

    return Math.floor(this.netCents / 100).toLocaleString(this.numberLocale);
  }

  getCentPrice() {
    return String(this.netCents % 100).padStart(2, '0');
  }

  selectProduct() {
    const subject = `Produktbestellung: ${this.t.instant(this.product.name)}`;

    if (this.product.isSelected) {
      return;
    }

    if (this.product.price === -1) {
      window.open(`mailto:info@alphabees.de?subject=${subject}`, '_blank');

      return;
    } else {
      const currentRetention = this.activeProduct?.retentionDays;
      const targetRetention = this.product.retentionDays;

      if (
        currentRetention != null &&
        targetRetention != null &&
        targetRetention < currentRetention
      ) {
        // Retention shrinks with this plan change — warn before checkout.
        // Backend applies a 30-day grace window before deleting older events.
        const confirmDialogRef = this.dialogService.open(
          ConfirmDialogComponent,
          {
            context: {
              title: 'dialogs.confirmDialog.planRetentionDowngrade.title',
              message: this.t.instant(
                'dialogs.confirmDialog.planRetentionDowngrade.message',
                {
                  currentDays: currentRetention,
                  targetDays: targetRetention,
                }
              ),
            },
          }
        );

        confirmDialogRef.onClose.subscribe((isConfirmed: boolean) => {
          if (isConfirmed) {
            this.openPaymentLink();
          }
        });

        return;
      }

      this.openPaymentLink();
    }
  }

  /** Guards the buttons while the upgrade preview is being fetched. */
  protected opening = false;

  private openPaymentLink() {
    if (this.opening) {
      return;
    }
    this.opening = true;
    // A subscription on a superseded price has no switch target in Stripe's
    // customer portal — the backend then offers the upgrade inside the portal
    // and we show its conditions. Everyone else (and any preview error) takes
    // the payment link as before.
    this.organisationService
      .previewPlanChange(this.product.id!, this.effectiveInterval)
      .pipe(finalize(() => (this.opening = false)))
      .subscribe({
        next: (preview) =>
          preview?.available
            ? this.openUpgradeDialog(preview)
            : this.checkBillingDetailsThenPay(),
        error: () => this.checkBillingDetailsThenPay(),
      });
  }

  /** A first purchase (no paid plan yet) goes through the billing data check:
   *  the first invoice is issued to what is on file, and it cannot be changed
   *  once issued. Existing subscribers already have invoices on that data and
   *  go straight on. */
  private checkBillingDetailsThenPay() {
    if ((this.activeProduct?.price ?? 0) > 0) {
      this.redirectToPaymentLink();
      return;
    }
    this.dialogService
      .open(BillingDetailsDialogComponent, { closeOnBackdropClick: false })
      .onClose.subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.redirectToPaymentLink();
        }
      });
  }

  /** `retried`: the billing dialog was already shown for a refusal — do not
   *  open it in a loop. */
  private redirectToPaymentLink(retried = false) {
    this.organisationService
      .getPaymentLink(this.product.id!, this.effectiveInterval)
      .subscribe({
        next: ({ payment_link }) => {
          // do not open in a new window, but in the same window
          window.open(payment_link, '_self');
        },
        error: (e: HttpErrorResponse) => {
          // The backend wants complete billing data before Checkout. Clients
          // who skipped the dialog above (paid plan, no Stripe subscription)
          // land here: ask for the data instead of showing an error.
          const code = e?.error?.detail?.code;
          const needsBillingData =
            code === 'BILLING_DETAILS_INCOMPLETE' || code === 'VAT_ID_REQUIRED';
          if (needsBillingData && !retried) {
            this.dialogService
              .open(BillingDetailsDialogComponent, {
                closeOnBackdropClick: false,
                context: { incomplete: true },
              })
              .onClose.subscribe((confirmed: boolean) => {
                if (confirmed) {
                  this.redirectToPaymentLink(true);
                }
              });
            return;
          }
          this.nbToastrService.warning(
            this.t.instant('dialogs.billingDetails.checkoutFailed'),
            this.t.instant('dialogs.billingDetails.checkoutFailedTitle')
          );
        },
      });
  }

  private openUpgradeDialog(preview: PlanChangePreview) {
    this.dialogService
      .open(PlanUpgradeDialogComponent, {
        closeOnBackdropClick: false,
        context: {
          planId: this.product.id,
          targetName: this.product.name,
          currentName: this.productService.getPlanById(preview.current_plan_id)
            ?.name,
          preview,
        },
      })
      .onClose.subscribe((upgraded: boolean) => {
        if (!upgraded) {
          return;
        }
        // Features and quotas of the new plan live in the token.
        this.authService.refreshToken().subscribe(() => {
          this.productService.getActivePlan().subscribe();
          this.organisationService.getOrganisation().subscribe();
          this.router.navigate(['/organisation'], {
            queryParams: { tab: 'billing' },
          });
          this.nbToastrService.success(
            this.t.instant('dialogs.planUpgrade.success')
          );
        });
      });
  }

  startTrial() {
    const confirmDialogRef = this.dialogService.open(ConfirmDialogComponent, {
      context: {
        title: 'dialogs.confirmDialog.startTrialPlan.title',
        message: 'dialogs.confirmDialog.startTrialPlan.message',
        onConfirm: async () => {
          this.organisationService.startTrialPlan().subscribe(() => {
            this.authService.refreshToken().subscribe(() => {
              confirmDialogRef.close();
              this.router.navigate(['/organisation']);
              this.nbToastrService.success(
                this.t.instant('components.trialPlanSuccess.trialPlanSuccess')
              );
            });
          });
        },
      },
    });
  }

  get canUseTrial() {
    if (isFreemailAddress(this.organisationService.organisation.email)) {
      return false;
    }

    // A paying client upgrades, they do not trial: the trial would replace
    // their plan and schedule a downgrade to Free while Stripe keeps charging.
    // The backend refuses it as well (TRIAL_NOT_AVAILABLE_FOR_SUBSCRIBERS).
    if ((this.activeProduct?.price ?? 0) > 0) {
      return false;
    }

    // Never offer a trial for the plan the client is already subscribed to —
    // starting it would swap a paid subscription for a trial plan.
    if (this.activeProduct?.id === this.product.id) {
      return false;
    }

    return this.organisationService.organisation?.trial_used === false;
  }

  get activeProduct() {
    return this.productService.activeProduct$.value;
  }

  /** Interval the client is actually billed at. Missing value = a subscription
   * sold before annual prices existed, which was always monthly. */
  get activeInterval(): 'month' | 'year' {
    return this.productService.currentPlan$.value?.billing_interval === 'year'
      ? 'year'
      : 'month';
  }

  /** True only when this card represents what the client is billed for RIGHT
   * NOW — same plan AND same interval. Matching on plan id alone marked a
   * monthly Basic as "Aktiv" while the toggle showed Jährlich, and
   * selectProduct() short-circuits on isSelected, so the upgrade was
   * unreachable. Plans without an annual price (free, enterprise) always
   * resolve to monthly on both sides, so they are unaffected. */
  get isSelected() {
    return (
      this.activeProduct?.id === this.product.id &&
      this.activeInterval === this.effectiveInterval
    );
  }

  /** Same plan, other interval — offer the switch instead of a bare "Auswählen". */
  get isIntervalSwitch(): boolean {
    return (
      this.activeProduct?.id === this.product.id &&
      this.hasAnnual &&
      this.activeInterval !== this.effectiveInterval
    );
  }

  get switchLabelKey(): string {
    return this.effectiveInterval === 'year'
      ? 'components.productCard.switchToYearly'
      : 'components.productCard.switchToMonthly';
  }
}
